import React from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { LineChart, Line, AreaChart, Area, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';
import { Clock, Zap, Activity, TrendingUp } from 'lucide-react';

interface PerformanceMetric {
  id: string;
  metric_type: 'page_load' | 'api_call' | 'component_render' | 'user_action';
  metric_name: string;
  value: number;
  unit: 'ms' | 'seconds' | 'bytes' | 'count';
  timestamp: string;
  metadata?: Record<string, any>;
}

interface PerformanceChartsProps {
  metrics: PerformanceMetric[];
}

export const PerformanceCharts: React.FC<PerformanceChartsProps> = ({ metrics }) => {
  // Process data for different chart types
  const processMetricsForTimeline = () => {
    const last24Hours = metrics.filter(metric => {
      const metricTime = new Date(metric.timestamp);
      const now = new Date();
      return (now.getTime() - metricTime.getTime()) < 24 * 60 * 60 * 1000;
    });

    // Group by hour
    const hourlyData = last24Hours.reduce((acc, metric) => {
      const hour = new Date(metric.timestamp).getHours();
      const key = `${hour}:00`;
      
      if (!acc[key]) {
        acc[key] = {
          hour: key,
          page_load: [],
          api_call: [],
          component_render: [],
          user_action: []
        };
      }
      
      acc[key][metric.metric_type].push(metric.value);
      return acc;
    }, {} as Record<string, any>);

    return Object.values(hourlyData).map((data: any) => ({
      hour: data.hour,
      page_load: data.page_load.length > 0 ? data.page_load.reduce((a: number, b: number) => a + b, 0) / data.page_load.length : 0,
      api_call: data.api_call.length > 0 ? data.api_call.reduce((a: number, b: number) => a + b, 0) / data.api_call.length : 0,
      component_render: data.component_render.length > 0 ? data.component_render.reduce((a: number, b: number) => a + b, 0) / data.component_render.length : 0,
      user_action: data.user_action.length > 0 ? data.user_action.reduce((a: number, b: number) => a + b, 0) / data.user_action.length : 0,
    }));
  };

  const processApiCallMetrics = () => {
    const apiCalls = metrics.filter(m => m.metric_type === 'api_call');
    const grouped = apiCalls.reduce((acc, metric) => {
      const endpoint = metric.metadata?.endpoint || 'unknown';
      if (!acc[endpoint]) {
        acc[endpoint] = { endpoint, total: 0, count: 0, success: 0, failure: 0 };
      }
      acc[endpoint].total += metric.value;
      acc[endpoint].count += 1;
      
      if (metric.metadata?.success) {
        acc[endpoint].success += 1;
      } else {
        acc[endpoint].failure += 1;
      }
      
      return acc;
    }, {} as Record<string, any>);

    return Object.values(grouped).map((data: any) => ({
      ...data,
      average: data.total / data.count,
      successRate: (data.success / data.count) * 100
    }));
  };

  const processMetricTypeDistribution = () => {
    const distribution = metrics.reduce((acc, metric) => {
      acc[metric.metric_type] = (acc[metric.metric_type] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);

    const colors = ['hsl(var(--primary))', 'hsl(var(--secondary))', 'hsl(var(--accent))', 'hsl(var(--muted))'];
    
    return Object.entries(distribution).map(([type, count], index) => ({
      name: type,
      value: count,
      color: colors[index % colors.length]
    }));
  };

  const timelineData = processMetricsForTimeline();
  const apiMetrics = processApiCallMetrics();
  const distributionData = processMetricTypeDistribution();

  // Calculate summary stats
  const avgPageLoad = metrics
    .filter(m => m.metric_type === 'page_load')
    .reduce((sum, m) => sum + m.value, 0) / Math.max(1, metrics.filter(m => m.metric_type === 'page_load').length);

  const avgApiCall = metrics
    .filter(m => m.metric_type === 'api_call')
    .reduce((sum, m) => sum + m.value, 0) / Math.max(1, metrics.filter(m => m.metric_type === 'api_call').length);

  const totalInteractions = metrics.filter(m => m.metric_type === 'user_action').length;

  return (
    <div className="space-y-6">
      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gjennomsnittlig sidelasting</CardTitle>
            <Clock className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgPageLoad.toFixed(0)}ms</div>
            <p className="text-xs text-muted-foreground">
              Siste 24 timer
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Gjennomsnittlig API-respons</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{avgApiCall.toFixed(0)}ms</div>
            <p className="text-xs text-muted-foreground">
              Alle endepunkter
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Brukerinteraksjoner</CardTitle>
            <Activity className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalInteractions}</div>
            <p className="text-xs text-muted-foreground">
              Totalt registrert
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Totale målinger</CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{metrics.length}</div>
            <p className="text-xs text-muted-foreground">
              Alle kategorier
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Performance Timeline */}
      <Card>
        <CardHeader>
          <CardTitle>Ytelsesutvikling over tid</CardTitle>
          <CardDescription>Gjennomsnittlig responstid per time siste 24 timer</CardDescription>
        </CardHeader>
        <CardContent>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={timelineData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="hour" />
              <YAxis />
              <Tooltip />
              <Legend />
              <Line 
                type="monotone" 
                dataKey="page_load" 
                stroke="hsl(var(--primary))" 
                name="Sidelasting (ms)"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="api_call" 
                stroke="hsl(var(--secondary))" 
                name="API-kall (ms)"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="user_action" 
                stroke="hsl(var(--accent))" 
                name="Brukerhandlinger (ms)"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* API Performance */}
        <Card>
          <CardHeader>
            <CardTitle>API-ytelse</CardTitle>
            <CardDescription>Responstid per endepunkt</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={apiMetrics}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="endpoint" />
                <YAxis />
                <Tooltip />
                <Bar dataKey="average" fill="hsl(var(--primary))" name="Gjennomsnitt (ms)" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Metric Distribution */}
        <Card>
          <CardHeader>
            <CardTitle>Målingsfordeling</CardTitle>
            <CardDescription>Antall målinger per kategori</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={distributionData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {distributionData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};